import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'

const ALLOWED_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/pdf',
  'text/plain'
]
const MAX_FILE_SIZE = 4 * 1024 * 1024
const UPLOAD_URL = '/rails/active_storage/direct_uploads'

// Direct-uploads files picked in the input and renders one row per file from the template.
// Each row carries data-state (uploading, uploaded, error) for its styling and a hidden input
// that is enabled with the blob's signed id once the upload finishes.
export default class extends Controller<HTMLElement> {
  static targets = ['dropzone', 'list', 'template']

  declare readonly dropzoneTarget: HTMLElement
  declare readonly listTarget: HTMLElement
  declare readonly templateTarget: HTMLTemplateElement

  upload (event: Event): void {
    const input = event.target as HTMLInputElement
    for (const file of Array.from(input.files ?? [])) this.add(file)
    input.value = ''
  }

  accept (event: CustomEvent<{ file: File }>): void {
    this.add(event.detail.file)
  }

  detach (event: Event): void {
    (event.target as HTMLElement).closest('[data-state]')?.remove()
  }

  dragEnter (): void {
    this.dropzoneTarget.classList.add('border-primary')
  }

  dragLeave (): void {
    this.dropzoneTarget.classList.remove('border-primary')
  }

  private add (file: File): void {
    const row = this.templateTarget.content.firstElementChild?.cloneNode(true) as HTMLElement
    this.slot(row, 'name').textContent = file.name
    this.listTarget.append(row)

    const errors = this.validate(file)
    if (errors.length > 0) {
      this.slot(row, 'errors').textContent = errors.join(', ')
      row.dataset.state = 'error'
      return
    }

    const directUpload = new DirectUpload(file, UPLOAD_URL, {
      directUploadWillStoreFileWithXHR: (request: XMLHttpRequest) => {
        request.upload.addEventListener('progress', (e) => {
          row.dataset.progress = Math.round((e.loaded / e.total) * 100).toString()
        })
      }
    })

    directUpload.create((error, blob) => {
      if (error) {
        this.slot(row, 'errors').textContent = error.message
        row.dataset.state = 'error'
        return
      }

      const blobId = this.slot<HTMLInputElement>(row, 'blob-id')
      blobId.value = blob.signed_id
      blobId.disabled = false
      row.dataset.state = 'uploaded'
    })
  }

  private validate (file: File): string[] {
    const errors: string[] = []
    if (!ALLOWED_TYPES.includes(file.type)) errors.push('File type must be .doc, .docx, .pdf, or .txt')
    if (file.size > MAX_FILE_SIZE) errors.push('File size must be less than 4mb')
    return errors
  }

  private slot<T extends HTMLElement = HTMLElement> (row: HTMLElement, name: string): T {
    return row.querySelector(`[data-slot="${name}"]`) as T
  }
}
